

































import { Component, Prop, Mixins } from "vue-property-decorator";
import { DataModules } from "@/store/modules/data";
import { GatewayContext } from "@/data/enums/gateway";
import { IBrand } from "@/models/brands";
import { IBrandGateway } from "@/models/gateways";
import { IClient } from "@/models/clients";
import { IState } from "@/store";
import { SelectedPaymentMethod } from "@/models/selectPaymentMethod";
import { mapState } from "vuex";
import Context from "@/mixins/context";
import ExternalAuthMixin from "@/components/app/global/payments/externalAuthMixin.vue";
import HasValidation from "@/mixins/HasValidation";
import RequestPromises from "@/mixins/requestPromises";
import { ClientRoutes } from "@/data/enums/router";

@Component({
  components: {
    "brand-gateways": () =>
      import("@/components/app/global/payments/brandGateways/index.vue")
  },
  computed: {
    ...mapState({
      dataScope(state: IState, getters) {
        return getters[`data/${this.dataModule}/clientScope`](this.clientId);
      },
      gateways(state, getters) {
        return getters[`data/listArray`]({
          storeModule: DataModules.BRAND_GATEWAYS
        });
      }
    })
  }
})
export default class AddPaymentMethodModal extends Mixins(
  RequestPromises,
  HasValidation,
  ExternalAuthMixin,
  Context
) {
  // Props

  @Prop({ required: true })
  public client!: IClient;

  @Prop({ required: true })
  public clientId!: IClient["id"];

  @Prop({ required: true })
  public brandId!: IBrand["id"];

  // Data
  public isProcessing = false;
  public paymentDetails: SelectedPaymentMethod | null = null;
  public dataModule: string = DataModules.CLIENTS_PAYMENT_DETAILS;
  public dataScope!: string;
  public gateways!: IBrandGateway[];
  public hasUnpaidInvoices = false;
  public isLoading = true;

  // Life-cycle methods

  public async created() {
    try {
      await Promise.all([
        this.getBrandGateways(),
        this.$store
          .dispatch(`data/${DataModules.INVOICES}/hasUnpaid`, {
            clientId: this.clientId
          })
          .then(value => (this.hasUnpaidInvoices = value))
      ]);
    } finally {
      this.isLoading = false;
    }
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binList", {
      storeModule: DataModules.BRAND_GATEWAYS,
      vm: this
    });
  }

  // Computed

  public get brandGatewayBindings() {
    return {
      autoSelectFirst: true,
      brandId: this.brandId,
      clientId: this.clientId,
      gatewayContext: GatewayContext.ADD,
      isDisabled: this.isProcessing,
      showAddManualPayment: true
    };
  }

  public get unpaidInvoicesRoute() {
    return this.isAdmin
      ? {
          name: "adminClientInvoicesUnpaid",
          params: { clientId: this.clientId }
        }
      : {
          name: ClientRoutes.INVOICES_UNPAID
        };
  }

  public get submitIsDisabled() {
    return (
      this.hasErrors ||
      this.isProcessing ||
      this.$_.isEmpty(this.paymentDetails)
    );
  }

  // Methods

  public openAddManualPaymentModal({ gatewayId }) {
    this.$emit("open-manual-payment-modal", {
      storedOnly: true,
      clientId: this.clientId,
      brandId: this.brandId,
      gatewayId
    });
    this.$emit("close");
  }

  public getBrandGateways() {
    return this.$store.dispatch(`data/${DataModules.BRAND_GATEWAYS}/list`, {
      brandId: this.brandId,
      params: {
        limit: 0,
        order: "order",
        "filter[active]": 1,
        with: "gateway.gateway_provider,gateway.card_types"
      },
      vm: this
    });
  }

  public async validateBeforeSubmit() {
    if (this.submitIsDisabled) return;

    try {
      if (!(await this.validate())) {
        throw this.$t("_sentence.form_is_not_valid");
      }

      this.isProcessing = true;

      const return_url = this.buildReturnUrl({
        externalAuthReturnLocation: this.$router.resolve(this.$route.fullPath)
          .location,
        paymentMethodType: this.paymentDetails?.type
      });

      const paymentDetailResult = await this.$store.dispatch(
        `data/${this.dataModule}/create`,
        {
          scope: this.dataScope,
          id: this.clientId,
          data: { ...this.$_.get(this.paymentDetails, "data"), return_url }
        }
      );

      const scaVerified = paymentDetailResult?.sca_verified;
      const nextAction = paymentDetailResult?.next_action;

      if (nextAction && !scaVerified) {
        return await this.redirectToExternalAuth({
          ...nextAction,
          returnLocation: this.$router.resolve(this.$route.fullPath).location
        });
      }

      this.$emit("payment-method-added");
      this.$emit("close");
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    } finally {
      this.isProcessing = false;
    }
  }
}
